import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { MenuItem } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';

import { FormattedMessage as T } from '@/components/FormattedMessage';
import classNames from 'classnames';
import { CLASSES } from '@/constants/classes';
import intl from 'react-intl-universal';

export function ContactsSuggestField({
  contactsList,
  initialContactId,
  selectedContactId,
  defaultTextSelect = intl.get('select_contact'),
  onContactSelected,

  selectedContactType = [],
  popoverFill = false,

  ...suggestProps
}: any) {
  // filteredContacts
  const contacts = useMemo(
    () =>
      contactsList.map((contact: any) => ({
        ...contact,
        _id: `${contact.id}_${contact.contact_type}`,
      })),
    [contactsList],
  );

  const initialContact = useMemo(
    () => contacts.find((a: any) => a.id === initialContactId),
    [initialContactId, contacts],
  );

  const [selecetedContact, setSelectedContact] = useState(
    initialContact || null,
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof selectedContactId !== 'undefined') {
      const contact = selectedContactId
        ? contacts.find((a: any) => a.id === selectedContactId)
        : null;
      setSelectedContact(contact);
    }
  }, [selectedContactId, contacts, setSelectedContact]);

  const contactRenderer = useCallback(
    (contact: any, { handleClick }: any) => (
      <MenuItem
        key={contact.id}
        text={contact.display_name}
        label={contact.formatted_contact_service}
        onClick={handleClick}
      />
    ),
    [],
  );

  const onContactSelect = useCallback(
    (contact: any) => {
      setSelectedContact({ ...contact });
      onContactSelected && onContactSelected(contact);
      setIsOpen(false);
    },
    [setSelectedContact, onContactSelected],
  );

  // Handle input click to open dropdown
  const handleInputClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleInputValueRenderer = (inputValue: any) => {
    if (inputValue) {
      return inputValue.display_name.toString();
    }
  };

  const filterContacts = (
    query: any,
    contact: any,
    index: any,
    exactMatch: any,
  ) => {
    const normalizedTitle = contact.display_name.toLowerCase();
    const normalizedQuery = query.toLowerCase();
    if (exactMatch) {
      return normalizedTitle === normalizedQuery;
    } else {
      return (
        `${contact.display_name} ${normalizedTitle}`.indexOf(normalizedQuery) >=
        0
      );
    }
  };

  // Merge input props with onClick handler
  const mergedInputProps = {
    placeholder: defaultTextSelect,
    onClick: handleInputClick,
    ...suggestProps.inputProps,
  };

  // Merge popover props with isOpen state
  const mergedPopoverProps = {
    minimal: true,
    boundary: 'window',
    isOpen: isOpen,
    onInteraction: (nextOpenState: any) => setIsOpen(nextOpenState),
    // Elevate the suggestions above the drawer overlay (z-index 5000); without
    // this the popover renders *under* the drawer (see App.scss
    // `.select-popover-in-drawer`).
    popoverClassName: 'select-popover-in-drawer',
    ...suggestProps.popoverProps,
  };

  return (
    <Suggest
      items={contacts}
      noResults={<MenuItem disabled={true} text={<T id={'no_results'} />} />}
      itemRenderer={contactRenderer}
      itemPredicate={filterContacts}
      onItemSelect={onContactSelect}
      selectedItem={selecetedContact}
      inputProps={mergedInputProps}
      resetOnClose={true}
      popoverProps={mergedPopoverProps}
      inputValueRenderer={handleInputValueRenderer}
      className={classNames(CLASSES.FORM_GROUP_LIST_SELECT, {
        [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
      })}
      {...suggestProps}
    />
  );
}
